import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import ModalShell from './ModalShell';
import { sa, PillButton } from '../superAdminUi';

// A design-system confirmation dialog on the Figma modal shell — a titled card, a message,
// and Cancel / Confirm actions. Drop-in for the legacy ConfirmPopupForm: it accepts the same
// prop names (isOpen/onOk/successPopupMessage/okButtonText/titleText) as well as the clearer
// open/onConfirm/message/confirmLabel/title aliases. Destructive confirms get the pink accent.
function ConfirmDialog({
  open,
  isOpen,
  onClose,
  onConfirm,
  onOk,
  title,
  titleText,
  message,
  successPopupMessage,
  confirmLabel,
  okButtonText,
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
}) {
  const isVisible = open ?? isOpen ?? false;
  const heading = title ?? titleText ?? 'Please confirm';
  const body = message ?? successPopupMessage ?? '';
  const confirm = confirmLabel ?? okButtonText ?? 'Confirm';
  const handleConfirm = onConfirm ?? onOk;

  return (
    <ModalShell
      open={isVisible}
      onClose={onClose}
      title={heading}
      accent={destructive ? sa.color.pink : sa.color.teal}
    >
      <Typography
        sx={{ fontFamily: sa.font.family, fontSize: 14, color: sa.color.textGray, mb: 3, lineHeight: 1.6 }}
      >
        {body}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <PillButton type='button' $variant='outline' $size='lg' onClick={onClose} disabled={loading}>
          {cancelLabel}
        </PillButton>
        <PillButton
          type='button'
          $variant={destructive ? 'danger' : 'teal'}
          $size='lg'
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : confirm}
        </PillButton>
      </Box>
    </ModalShell>
  );
}

ConfirmDialog.propTypes = {
  open: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  onOk: PropTypes.func,
  title: PropTypes.node,
  titleText: PropTypes.node,
  message: PropTypes.node,
  successPopupMessage: PropTypes.node,
  confirmLabel: PropTypes.string,
  okButtonText: PropTypes.string,
  cancelLabel: PropTypes.string,
  destructive: PropTypes.bool,
  loading: PropTypes.bool,
};

export default ConfirmDialog;
